import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import Chip from '@widget/other/Chip';
import { Dropdown } from '@widget/popups/Dropdown';
import { getInputSizeStyles, getInputStateClass } from './utils';
import useDebounce from '@hooks/useDebounce';
import { UXPContext } from '@core/context/Context';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import { hasValue } from '../../../../utils';
/**
 * @export
 * A tag input that stores values as a list of string chips.
 *
 * Users type into an inline text field and press Enter or comma (`,`) to add a tag.
 * Existing tags are displayed as removable `Chip` components. Pressing Backspace
 * on an empty field removes the last tag.
 *
 * An optional `options` prop enables a suggestion dropdown. It accepts either a
 * static `string[]` (filtered client-side) or an async function that resolves to
 * `string[]` (called with a 300ms debounce). Already-added tags are excluded from
 * suggestions. Arrow keys navigate the dropdown; Enter selects the highlighted item.
 *
 * @example
 * ```tsx
 * // Basic — free-form tags only
 * <TagInput
 *   value={tags}
 *   onChange={setTags}
 *   placeholder="Add tags..."
 * />
 *
 * // With async suggestions
 * <TagInput
 *   value={tags}
 *   onChange={setTags}
 *   options={async (text) => {
 *     const res = await fetchTags(text);
 *     return res.map(t => t.name);
 *   }}
 * />
 * ```
 */
const TagInput = (props) => {
    const { value = [], onChange, options, placeholder, delimiters = [','], className, disabled, readOnly, isValid, fullWidth, minWidth, maxWidth, width, addNewValues, } = props;
    const context = React.useContext(UXPContext);
    const [inputText, setInputText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [savingNewValue, setSavingNewValue] = useState(false);
    /* Tracks whether the input is currently focused. Used to gate the
     * suggestion-loading effect so it doesn't auto-open the dropdown on
     * mount or while the field is blurred. Ref (not state) so toggling
     * doesn't trigger re-renders or re-fire the debounce effect. */
    const isFocusedRef = useRef(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debouncedInput = useDebounce(inputText, 300);
    // Returns true when `tag` is already in `arr` (case-sensitive, exact match)
    const hasTag = (arr, tag) => arr.indexOf(tag) !== -1;
    const addNewAlwaysShow = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) && !!addNewValues.alwaysShow;
    // Resolve suggestions after a 300ms debounce. Fires on every debouncedInput
    // change but only updates the dropdown when the input is currently focused
    // — otherwise we'd auto-open the dropdown on mount or while another field
    // is interacting (see the showcase regression where every TagInput popped
    // open simultaneously).
    //
    // While focused: empty input → all options (minus already-added tags);
    // typed input → substring filter. The dropdown stays open as the user
    // backspaces to empty.
    useEffect(() => {
        if (!isFocusedRef.current)
            return;
        if (!options) {
            setSuggestions([]);
            setIsDropdownOpen(!!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
            setActiveIndex(-1);
            return;
        }
        if (Array.isArray(options)) {
            const filtered = debouncedInput.trim()
                ? options.filter(o => o.toLowerCase().indexOf(debouncedInput.toLowerCase()) !== -1 && !hasTag(value, o))
                : options.filter(o => !hasTag(value, o));
            setSuggestions(filtered);
            setIsDropdownOpen(filtered.length > 0 || !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
            setActiveIndex(-1);
        }
        else {
            options(debouncedInput)
                .then(results => {
                if (!isFocusedRef.current)
                    return;
                const filtered = results.filter(r => !hasTag(value, r));
                setSuggestions(filtered);
                setIsDropdownOpen(filtered.length > 0 || !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
                setActiveIndex(-1);
            })
                .catch(() => {
                if (!isFocusedRef.current)
                    return;
                setSuggestions([]);
                setIsDropdownOpen(!!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
                setActiveIndex(-1);
            });
        }
    }, [debouncedInput]);
    // Scroll the active suggestion into view
    useEffect(() => {
        if (activeIndex < 0 || !suggestionsRef.current)
            return;
        const item = suggestionsRef.current.children[activeIndex];
        if (item)
            item.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);
    const addTag = useCallback((tag) => {
        var _a;
        const trimmed = tag.trim();
        if (!trimmed || hasTag(value, trimmed))
            return;
        onChange([...value, trimmed]);
        setInputText('');
        setSuggestions([]);
        setIsDropdownOpen(false);
        setActiveIndex(-1);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [value, onChange]);
    const removeTag = useCallback((index) => {
        var _a;
        const next = [...value];
        next.splice(index, 1);
        onChange(next);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [value, onChange]);
    function handleKeyDown(e) {
        const addNewSlotVisible = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) &&
            (addNewAlwaysShow || suggestions.length === 0);
        const dropdownVisible = isDropdownOpen && (suggestions.length > 0 || addNewSlotVisible);
        const totalItems = suggestions.length + (addNewSlotVisible ? 1 : 0);
        // ArrowDown/Up: cycle through suggestion dropdown items
        // Enter: add highlighted suggestion, fire add-new action, or add typed text as a free-form tag
        // Backspace on empty input: remove the last tag
        // Escape: close the dropdown without adding a tag
        if (e.key === 'ArrowDown') {
            if (!dropdownVisible || totalItems === 0)
                return;
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % totalItems);
        }
        else if (e.key === 'ArrowUp') {
            if (!dropdownVisible || totalItems === 0)
                return;
            e.preventDefault();
            setActiveIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (dropdownVisible && activeIndex >= 0 && activeIndex < suggestions.length) {
                addTag(suggestions[activeIndex]);
            }
            else if (dropdownVisible && addNewSlotVisible && activeIndex === suggestions.length) {
                triggerAddNew();
            }
            else if (inputText.trim()) {
                addTag(inputText);
            }
        }
        else if (e.key === 'Backspace' && !inputText && value.length > 0) {
            removeTag(value.length - 1);
        }
        else if (e.key === 'Escape') {
            setIsDropdownOpen(false);
            setSuggestions([]);
            setActiveIndex(-1);
        }
    }
    function triggerAddNew() {
        var _a;
        if (!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable))
            return;
        if (addNewValues.onClick) {
            addNewValues.onClick();
            setIsDropdownOpen(false);
            return;
        }
        if (!hasValue(inputText))
            return;
        setSavingNewValue(true);
        Promise.resolve((_a = addNewValues.onAddNewValue) === null || _a === void 0 ? void 0 : _a.call(addNewValues, inputText))
            .then((res) => {
            const next = typeof res === 'string' && hasValue(res) ? res : inputText;
            addTag(next);
        })
            .catch(() => { })
            .finally(() => setSavingNewValue(false));
    }
    function handleChange(e) {
        const val = e.target.value;
        // Any character in `delimiters` acts as a submit shortcut: strip the trailing
        // delimiter and add the tag immediately. Defaults to comma; pass `[' ']`
        // for whitespace-separated tags, etc.
        if (val.length > 0 && delimiters.includes(val.slice(-1))) {
            const tag = val.slice(0, -1).trim();
            if (tag)
                addTag(tag);
            else
                setInputText('');
            return;
        }
        setInputText(val);
        setActiveIndex(-1);
    }
    function handleFocus() {
        var _a;
        isFocusedRef.current = true;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('uxpcore_input--focus');
        /* Open the dropdown on focus — same behaviour as Select / AutoComplete.
         * Show the full suggestion list (minus already-added tags) for static
         * arrays; trigger the resolver with an empty query for async sources;
         * otherwise (no `options`) open only if the add-new affordance is
         * configured so the user can still see it. */
        if (Array.isArray(options)) {
            const filtered = options.filter(o => !hasTag(value, o));
            setSuggestions(filtered);
            setIsDropdownOpen(filtered.length > 0 || !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
            setActiveIndex(-1);
        }
        else if (typeof options === 'function') {
            options('')
                .then(results => {
                const filtered = results.filter(r => !hasTag(value, r));
                setSuggestions(filtered);
                setIsDropdownOpen(filtered.length > 0 || !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
                setActiveIndex(-1);
            })
                .catch(() => {
                setSuggestions([]);
                setIsDropdownOpen(!!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable));
                setActiveIndex(-1);
            });
        }
        else if (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) {
            setSuggestions([]);
            setIsDropdownOpen(true);
            setActiveIndex(-1);
        }
    }
    function handleBlur() {
        var _a;
        isFocusedRef.current = false;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove('uxpcore_input--focus');
        // Commit any pending text as a tag so it isn't silently lost when focus leaves.
        if (inputText.trim()) {
            addTag(inputText);
        }
    }
    function handleContainerClick(e) {
        var _a, _b;
        if (disabled || readOnly)
            return;
        /* Portaled descendants (the Dropdown backdrop in particular) bubble
         * their synthetic events through the React tree to this container.
         * Without this guard, clicking the backdrop to close the dropdown
         * would refocus the input here and immediately reopen it. Only act
         * when the click actually originated inside the container's DOM. */
        if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)))
            return;
        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    }
    const sizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    const showAddNewFooter = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) &&
        (addNewAlwaysShow || suggestions.length === 0);
    /* Suggestions scroll inside their own container; the "+ Add new"
     * button lives as a sibling outside the scroll area so it stays
     * pinned at the bottom of the dropdown regardless of list length. */
    const dropdownContent = (_jsxs("div", { className: "uxpcore_taginput__dropdown", children: [_jsx("div", { className: "uxpcore_taginput__suggestions", ref: suggestionsRef, children: suggestions.map((s, i) => (_jsx("div", { className: classNames('uxpcore_taginput__suggestion-item', {
                        'uxpcore_taginput__suggestion-item--active': i === activeIndex,
                    }), onMouseDown: (e) => {
                        e.preventDefault(); // prevent input blur before click fires
                        addTag(s);
                    }, onMouseEnter: () => setActiveIndex(i), children: s }, i))) }), showAddNewFooter && (_jsx("div", { className: "uxpcore_dropdown-action-row", onMouseDown: (e) => {
                    /* Block input blur and own the click for the whole
                     * row; inner button is purely visual. */
                    e.preventDefault();
                    if (!savingNewValue)
                        triggerAddNew();
                }, children: _jsx(ButtonComponent, { title: savingNewValue && addNewValues.loadingTitle
                        ? addNewValues.loadingTitle
                        : addNewValues.title, onClick: () => { }, disabled: savingNewValue, size: "smaller", variant: "link" }) }))] }));
    return (_jsxs("div", { ref: containerRef, className: classNames('uxpcore_taginput', 'uxpcore_input', className, stateClass, {
            'uxpcore_taginput--has-tags': value.length > 0,
            'uxpcore_input--full-width': fullWidth,
        }), style: sizeStyles, onClick: handleContainerClick, children: [_jsxs("div", { className: "uxpcore_taginput__inner", ref: triggerRef, children: [value.map((tag, i) => (_jsx(Chip, Object.assign({ label: tag, size: "small", variant: "secondary" }, (!disabled && !readOnly ? {
                        icon: 'fas times',
                        iconPosition: 'right',
                        onClick: (e) => {
                            e.stopPropagation();
                            removeTag(i);
                        }
                    } : {})), i))), _jsx("input", { ref: inputRef, className: "uxpcore_taginput__input", value: inputText, onChange: handleChange, onKeyDown: handleKeyDown, onFocus: handleFocus, onBlur: handleBlur, placeholder: value.length === 0 ? (placeholder || '') : '', disabled: disabled, readOnly: readOnly, dir: context.direction || 'ltr' })] }), (options || (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable)) && (_jsx(Dropdown, { externalTriggerRef: triggerRef, content: dropdownContent, isOpen: isDropdownOpen && (suggestions.length > 0 || showAddNewFooter), onToggle: () => setIsDropdownOpen(false), position: "bottom-left", matchTriggerWidth: true }))] }));
};
export default TagInput;
